import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdAttachMoney,
  MdFilterListOff,
  MdRefresh,
  MdSync,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { ResponsiveDataList } from '@components/ui/ResponsiveDataList';
import { TabFilter } from '@components/ui/TabFilter';
import { useCacheSync } from '@features/cache/hooks';
import { MarketPriceCard } from '@features/marketPrices/components/MarketPriceCard';
import { MarketPriceEditDialog } from '@features/marketPrices/components/MarketPriceEditDialog';
import { MarketPricesGrid } from '@features/marketPrices/components/MarketPricesGrid';
import {
  MarketPriceFiltersBar,
  type MarketPriceFilters,
} from '@features/marketPrices/components/MarketPriceFiltersBar';
import {
  DATE_PRESETS,
  DEFAULT_PRESET_KEY,
  detectActivePreset,
  formatDayShort,
} from '@features/marketPrices/dateRange';
import { useMarketPrices } from '@features/marketPrices/hooks';
import { useApmcOptions } from '@features/apmcs/hooks';
import { useCropOptions } from '@features/crops/hooks';
import { useVarietyOptions } from '@features/varieties/hooks';
import type {
  ListMarketPricesParams,
  MarketPrice,
  MarketPriceSortKey,
  SortDirection,
} from '@features/marketPrices/schema';

const DEFAULT_PAGE_SIZE = 50;

const EMPTY_FILTERS: MarketPriceFilters = {
  cropId: null,
  apmcId: null,
  varietyId: null,
  dateFrom: '',
  dateTo: '',
};

// Pending = is_active=false rows (today's morning_placeholder stubs awaiting
// fill-in by the business team). Published = the live data. Default to Pending
// because that's the team's primary daily workflow when they open this screen.
type MarketPriceTabValue = 'pending' | 'published';

const MARKET_PRICE_TABS: ReadonlyArray<{ value: MarketPriceTabValue; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'published', label: 'Published' },
];

export default function MarketPricesScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<MarketPriceFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<{
    sortBy?: MarketPriceSortKey;
    sortDir?: SortDirection;
  }>({});
  const [editingRow, setEditingRow] = useState<MarketPrice | null>(null);
  const [tab, setTab] = useState<MarketPriceTabValue>('pending');

  // Reset to page 1 whenever filters / sort / tab change.
  useEffect(() => {
    setPage(1);
  }, [filters, pageSize, sort, tab]);

  const params = useMemo<ListMarketPricesParams>(
    () => ({
      page,
      pageSize,
      cropId: filters.cropId ?? undefined,
      apmcId: filters.apmcId ?? undefined,
      varietyId: filters.varietyId ?? undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
      isActive: tab === 'pending' ? 'false' : 'true',
    }),
    [page, pageSize, filters, sort, tab],
  );

  const { data, isLoading, isFetching, refetch } = useMarketPrices(params);
  const cropOptionsQuery = useCropOptions();
  const apmcOptionsQuery = useApmcOptions();
  const varietyOptionsQuery = useVarietyOptions();
  const cacheSyncMutation = useCacheSync('market-price');
  const filterOptionsLoading =
    cropOptionsQuery.isLoading ||
    apmcOptionsQuery.isLoading ||
    varietyOptionsQuery.isLoading;

  // Stable empty-array fallback so MarketPriceFiltersBar's downstream memos
  // (cropById / apmcById / varietyOptions) don't bust on every render during
  // the initial load. Mirrors the pattern in PriceSeriesScreen.
  const cropOptions = useMemo(
    () => cropOptionsQuery.data ?? [],
    [cropOptionsQuery.data],
  );
  const apmcOptions = useMemo(
    () => apmcOptionsQuery.data ?? [],
    [apmcOptionsQuery.data],
  );
  const varietyOptions = useMemo(
    () => varietyOptionsQuery.data ?? [],
    [varietyOptionsQuery.data],
  );

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters =
    filters.cropId !== null ||
    filters.apmcId !== null ||
    filters.varietyId !== null ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '';
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  // Active preset key (or null for custom / blank ranges). When both From and
  // To are blank, the API applies the 30-day default — flag that as the
  // default preset so the UI tells the truth about what's being shown.
  const activePresetKey =
    !filters.dateFrom && !filters.dateTo
      ? DEFAULT_PRESET_KEY
      : detectActivePreset(filters.dateFrom, filters.dateTo);

  // Range label shown in the footer. Defaults preset → preset label;
  // custom range → "From – To"; blank → falls back to the default preset.
  const rangeLabel = (() => {
    if (activePresetKey) {
      const p = DATE_PRESETS.find((d) => d.key === activePresetKey);
      return p?.label ?? '';
    }
    if (filters.dateFrom && filters.dateTo) {
      return `${formatDayShort(filters.dateFrom)} – ${formatDayShort(filters.dateTo)}`;
    }
    if (filters.dateFrom) return `From ${formatDayShort(filters.dateFrom)}`;
    if (filters.dateTo) return `Up to ${formatDayShort(filters.dateTo)}`;
    return '';
  })();

  return (
    <Page
      title="Market prices"
      subtitle="Daily mandi observations for crops."
      fullHeight
      headerRightSlot={
        <div className="flex items-center gap-3">
          <Button
            variant="contained"
            size="small"
            startIcon={cacheSyncMutation.isPending ? undefined : <MdSync />}
            onClick={() => cacheSyncMutation.mutate()}
            disabled={cacheSyncMutation.isPending}
            sx={{
              minWidth: '120px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {cacheSyncMutation.isPending ? (
              <>
                <CircularProgress size={14} sx={{ mr: 0.75 }} />
                Syncing…
              </>
            ) : (
              'Sync'
            )}
          </Button>
          <TabFilter
            options={MARKET_PRICE_TABS}
            current={tab}
            onChange={setTab}
            ariaLabel="Filter by status"
          />
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-1.5 px-1">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
          Date range
        </span>
        {DATE_PRESETS.map((preset) => {
          const isActive = activePresetKey === preset.key;
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => setFilters({ ...filters, ...preset.resolve() })}
              className={[
                'rounded-full px-3 py-1 text-[12px] font-medium transition-colors',
                isActive
                  ? 'bg-primary/[0.14] text-primary ring-1 ring-primary/[0.3]'
                  : 'bg-surface text-foreground-muted ring-1 ring-border hover:bg-primary/[0.06] hover:text-foreground',
              ].join(' ')}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <MarketPriceFiltersBar
          value={filters}
          onChange={setFilters}
          crops={cropOptions}
          apmcs={apmcOptions}
          varieties={varietyOptions}
          loading={filterOptionsLoading}
        />
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[95px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            {hasActiveFilters && (
              <Button variant="text" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {/* Refetch indicator: visible during filter changes / pagination /
            manual refresh. The grid keeps showing previous data underneath
            (placeholderData) so the user has spatial context. */}
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '130px', bar: 'w-20' },
              { width: '160px', bar: 'w-3/4' },
              { width: '160px', bar: 'w-2/3' },
              { width: '2fr', bar: 'w-3/4' },
              { width: '110px', bar: 'w-12' },
              { width: '120px', bar: 'w-16' },
              { width: '110px', bar: 'w-12' },
              { width: '110px', bar: 'w-14' },
              { width: '80px', bar: 'w-8' },
              { width: '200px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="No observations match the current filters. Try widening the date range or clearing crop / mandi."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : tab === 'pending' ? (
            <PageEmpty
              icon={MdAttachMoney}
              title="All caught up — no pending placeholders"
              message="When the morning script ingests new placeholders, they'll show up here for fill-in. Switch to Published to browse already-published prices."
            />
          ) : (
            <PageEmpty
              icon={MdAttachMoney}
              title={`No market prices in the ${rangeLabel.toLowerCase()}`}
              message="Once daily observations are ingested, they'll appear here. Adjust the date range to look further back."
            />
          )
        ) : (
          <ResponsiveDataList
            rows={data?.rowData ?? []}
            getRowId={(r) => r.id}
            desktop={
              <MarketPricesGrid
                data={data}
                onSortChange={setSort}
                onEdit={setEditingRow}
              />
            }
            renderCard={(row) => (
              <MarketPriceCard row={row} onClick={() => setEditingRow(row)} />
            )}
          />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
        extra={rangeLabel || undefined}
      />

      <MarketPriceEditDialog
        open={!!editingRow}
        row={editingRow}
        onClose={() => setEditingRow(null)}
      />
    </Page>
  );
}
